import { useTranslation } from "react-i18next";
import { Crosshair } from "lucide-react";
import type { Box01 } from "@/lib/face-scan/ink-find";
import type { InkLock } from "@/lib/face-scan/ink";

// FaceLock Ink Scan: what is drawn over the camera preview (owner, 2026-10-07:
// the scan must LOCATE the tattoo and LOCK ON to it, and the person should
// see that it did). Three things:
//   * the guide box: where the mark goes, exactly the area the judge counts as
//     centred (the centre plus or minus its tolerance, which relaxes with
//     time). The old box sat at 20% of the screen, not of the frame the judge
//     reads, and the two differed once the preview was cropped to fill;
//   * the brackets: the box the finder locked on to. Amber while something is
//     left to change (found), green when only the wait is left (locked);
//     none while it is still looking;
//   * the pill at the top: "Looking…", "Found it", "Locked on", in words.
//
// The boxes are fractions of the UNMIRRORED camera frame. The drawing is an SVG
// in the frame's own pixels with preserveAspectRatio "slice", which crops the
// way the preview's object-cover does, and it is flipped with the same
// scaleX(-1) as the front camera's preview: no maths to get wrong. No text is
// ever inside the flipped SVG (it would read backwards); the pill is HTML.

/** The colours of the three lock states (amber and green as the rest of the scan's hints). */
const INK_LOCK_COLOUR: Record<InkLock, string> = { searching: "#ffffff", found: "#f5c451", locked: "#3ddc84" };

const PILL: Record<InkLock, string> = {
  searching: "bg-black/60 text-white",
  found: "bg-[#f5c451] text-black",
  locked: "bg-[#3ddc84] text-black",
};

/** The four corners of a unit box (the brackets' group is moved and stretched onto the mark). */
const CORNERS = ["M0 0.28V0H0.28", "M0.72 0H1V0.28", "M1 0.72V1H0.72", "M0.28 1H0V0.72"];

export default function InkOverlay({ width, height, mirrored, lock, box, tol }: {
  /** The camera frame's size (video.videoWidth / videoHeight). */
  width: number;
  height: number;
  /** The preview is mirrored (the front camera): so is the drawing. */
  mirrored: boolean;
  lock: InkLock;
  /** The locked mark (fractions of the unmirrored frame), or null. */
  box: Box01 | null;
  /** The guide box: the centre plus or minus this (fractions of each side). */
  tol: number;
}) {
  const { t } = useTranslation();
  const colour = INK_LOCK_COLOUR[lock];
  const brackets = lock !== "searching" && box;
  return (
    <div className="pointer-events-none absolute inset-0" data-testid="ink-overlay" data-lock={lock}>
      <svg
        aria-hidden
        className="absolute inset-0 h-full w-full"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid slice"
        style={mirrored ? { transform: "scaleX(-1)" } : undefined}
        data-testid="ink-overlay-svg"
        data-mirrored={mirrored ? "1" : "0"}
      >
        <rect
          data-testid="ink-guide"
          x={(0.5 - tol) * width}
          y={(0.5 - tol) * height}
          width={2 * tol * width}
          height={2 * tol * height}
          rx={0.02 * Math.min(width, height)}
          fill="none"
          stroke={colour}
          strokeOpacity={lock === "searching" ? 0.7 : 0.9}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
        {brackets && (
          <g
            data-testid="ink-brackets"
            data-box={`${box.x.toFixed(3)},${box.y.toFixed(3)},${box.w.toFixed(3)},${box.h.toFixed(3)}`}
            // A unit box moved and stretched onto the mark: the move glides (120 ms) as the lock follows it.
            style={{
              transform: `translate(${box.x * width}px, ${box.y * height}px) scale(${box.w * width}, ${box.h * height})`,
              transition: "transform 120ms linear",
            }}
          >
            {CORNERS.map((d) => (
              <path key={d} d={d} fill="none" stroke={colour} strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            ))}
          </g>
        )}
      </svg>
      <div className="absolute inset-x-0 top-3 flex justify-center">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[15px] font-semibold shadow ${PILL[lock]}`}
          data-testid="ink-lock"
          data-lock={lock}
          aria-live="polite"
        >
          <Crosshair className="h-4 w-4" aria-hidden />
          {t(`faceLock.ink.lock.${lock}`)}
        </span>
      </div>
    </div>
  );
}
